import { MyLocation, Send } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  FormControl,
  FormLabel,
  InputAdornment,
  Typography,
} from "@mui/material";
import {
  createMissionPatient,
  type Mission,
  type PatientTransportAid,
} from "@rescuetablet/missions-client";
import {
  Field,
  Form,
  Formik,
  useFormikContext,
  type FormikHelpers,
} from "formik";
import { CheckboxWithLabel, TextField } from "formik-mui";
import "mapbox-gl/dist/mapbox-gl.css";
import { nanoid } from "nanoid";
import { useSnackbar } from "notistack";
import { useCallback, useId } from "react";
import { Map, Marker } from "react-map-gl";
import { useNavigate, useOutletContext, useParams } from "react-router";
import * as yup from "yup";
import GeolocateButton from "./GeolocateButton";
import LinkBehavior from "./LinkBehavior";

export const transportAidValues: Array<keyof PatientTransportAid> = [
  "walker",
  "wheelchair",
  "stretcher",
  "buggy",
  "ventilator",
];

const transportAidLabels: Record<keyof PatientTransportAid, string> = {
  walker: "Rollator",
  wheelchair: "Rollstuhl",
  stretcher: "Trage",
  buggy: "Kinderwagen",
  ventilator: "Beatmungsgerät",
};

const schema = yup.object({
  name: yup.string().required(),
  dateOfBirthOrAge: yup.string(),
  address: yup.string().required(),
  floor: yup.string(),
  elevator: yup.boolean().required(),
  phone: yup.string(),
  email: yup.string().email(),
  weight: yup.string(),
  disability: yup.string(),
  companion: yup.string(),
  diseases: yup.string(),
  additionalNotes: yup.string(),
  transportAid: yup.object(
    Object.fromEntries(
      transportAidValues.map((value) => [value, yup.boolean().required()])
    )
  ),
  coordinates: yup
    .object({
      latitude: yup.number().required(),
      longitude: yup.number().required(),
    })
    .optional()
    .default(undefined),
});

type Values = yup.InferType<typeof schema>;

const initialValues: Values = {
  name: "",
  dateOfBirthOrAge: "",
  address: "",
  floor: "",
  elevator: false,
  phone: "",
  email: "",
  weight: "",
  disability: "",
  companion: "",
  diseases: "",
  additionalNotes: "",
  transportAid: Object.fromEntries(
    transportAidValues.map((value) => [value, false])
  ),
  coordinates: undefined,
};

export default function PatientForm() {
  const { mission } = useOutletContext<{ mission: Mission }>();
  const { patientId = nanoid() } = useParams<{ patientId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const onSave = useCallback(
    (id: string) => {
      enqueueSnackbar({
        variant: "success",
        message: `Patient wurde erfolgreich angelegt. (ID: ${id})`,
      });
      navigate("..");
    },
    [enqueueSnackbar, navigate]
  );

  return (
    <Card>
      <PatientFormContent
        key={patientId}
        patientId={patientId}
        mission={mission}
        onSave={onSave}
      />
    </Card>
  );
}

function PatientFormContent({
  patientId,
  mission,
  onSave,
}: {
  patientId: string;
  mission: Mission;
  onSave: (patientId: string) => void;
}) {
  const formId = useId();

  const onSubmit = useCallback(
    async (
      {
        address,
        floor,
        elevator,
        phone,
        email,
        weight,
        disability,
        companion,
        diseases,
        additionalNotes,
        ...values
      }: Values,
      { setStatus, setSubmitting }: FormikHelpers<Values>
    ) => {
      setStatus(undefined);

      try {
        const notes: Array<string> = [];
        if (floor)
          notes.push(
            `Stockwerk: ${floor} (Aufzug ${elevator ? "vorhanden" : "nicht vorhanden"})`
          );
        if (weight) notes.push(`Gewicht: ${weight}`);
        if (phone) notes.push(`Telefon: ${phone}`);
        if (email) notes.push(`E-Mail: ${email}`);
        if (disability) notes.push(`Behinderung: ${disability}`);
        if (companion) notes.push(`Begleitperson: ${companion}`);
        if (diseases) notes.push(`Ansteckende Krankheiten: ${diseases}`);
        if (additionalNotes) notes.push(`Bemerkungen:\n${additionalNotes}`);

        await createMissionPatient({
          path: {
            missionId: mission.id,
            patientId,
          },
          body: {
            ...values,
            location: address,
            address: address?.replace(/(\r?\n)+/g, ", "),
            notes: notes.join("\n\n"),
          },
          throwOnError: true,
        });
        onSave(patientId);
      } catch (error: unknown) {
        setStatus(error);
        setSubmitting(false);
      }
    },
    [patientId, mission.id, onSave]
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {({ isValid, isSubmitting, dirty }) => (
        <>
          <CardContent>
            <Form id={formId} noValidate>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="h6">Neuer Patient</Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Field
                      name="name"
                      component={TextField}
                      label="Vor- und Nachname"
                      required
                    />
                    <Field
                      name="dateOfBirthOrAge"
                      component={TextField}
                      label="Geburtsdatum oder Alter"
                    />
                    <AddressField />
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Field
                        name="floor"
                        component={TextField}
                        label="Stockwerk"
                      />
                      <Field
                        name="elevator"
                        component={CheckboxWithLabel}
                        type="checkbox"
                        Label={{ label: "Aufzug vorhanden" }}
                      />
                    </Box>
                  </Box>
                  <MiniMap />
                </Box>
                <Field name="weight" component={TextField} label="Gewicht" />
                <Field
                  name="disability"
                  component={TextField}
                  label="Behinderung"
                />
                <Field
                  name="companion"
                  component={TextField}
                  label="Begleitperson"
                  helperText="Falls ja, bitte Namen und Verhältnis zum Patienten angeben."
                />
                <Field
                  name="phone"
                  component={TextField}
                  type="tel"
                  label="Telefon"
                />
                <Field
                  name="email"
                  component={TextField}
                  type="email"
                  label="E-Mail"
                />
                <Field
                  name="diseases"
                  component={TextField}
                  label="Ansteckende Krankheiten"
                />
                <Field
                  name="additionalNotes"
                  component={TextField}
                  label="Bemerkungen"
                  multiline
                  InputProps={{ rows: 3 }}
                />
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Transporthilfe</FormLabel>
                  <Box
                    sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    {transportAidValues.map((value) => (
                      <Field
                        key={value}
                        name={`transportAid.${value}`}
                        component={CheckboxWithLabel}
                        type="checkbox"
                        Label={{ label: transportAidLabels[value] }}
                      />
                    ))}
                  </Box>
                </FormControl>
              </Box>
            </Form>
          </CardContent>
          <CardActions>
            <Button component={LinkBehavior} href=".." variant="text">
              Abbrechen
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="contained"
              color="secondary"
              startIcon={<Send />}
              disabled={!isValid || !dirty}
              loading={isSubmitting}
            >
              Patient anlegen
            </Button>
          </CardActions>
        </>
      )}
    </Formik>
  );
}

function AddressField() {
  const {
    values: { address },
    setFieldValue,
    setFieldError,
  } = useFormikContext<Values>();

  return (
    <Field
      name="address"
      component={TextField}
      label="Adresse"
      multiline
      fullWidth
      slotProps={{
        input: {
          rows: 3,
          endAdornment: (
            <InputAdornment position="end">
              <GeolocateButton
                address={address ?? ""}
                setAddress={(value) => setFieldValue("address", value)}
                setCoordinates={(coordinates) =>
                  setFieldValue("coordinates", coordinates)
                }
                setError={(error) => setFieldError("address", error)}
              />
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

function MiniMap() {
  const {
    values: { coordinates },
  } = useFormikContext<Values>();

  return (
    <Box sx={{ width: 300, height: 300 }}>
      {coordinates ? (
        <Map
          mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
          reuseMaps
          latitude={coordinates.latitude}
          longitude={coordinates.longitude}
          zoom={16}
          mapStyle="mapbox://styles/mapbox/standard"
        >
          <Marker
            latitude={coordinates.latitude}
            longitude={coordinates.longitude}
            anchor="center"
          >
            <MyLocation />
          </Marker>
        </Map>
      ) : null}
    </Box>
  );
}
